// Mobile background audio.
//
// iOS suspends Web Audio (AudioContext/AudioWorklet) when the browser tab is
// backgrounded, but keeps a plain <audio> element playing. This module owns
// one hidden <audio> element and plays a rendered-mix WAV Blob through it —
// it knows nothing about multitrack, just plain element control.

export interface BackgroundAudio {
  /** Set src to an object-URL for blob and seek to positionSec. */
  arm(blob: Blob, positionSec: number): void;
  play(): void;
  pause(): void;
  seek(sec: number): void;
  /**
   * Silence the element without pausing it.
   *
   * The element must be started inside the Play tap — that is the only moment
   * WebKit unlocks it — but during the load window the LIVE engine is the audible
   * source, so the element runs muted until the handoff (B-260728 / plan A).
   */
  setMuted(muted: boolean): void;
  /**
   * Resolves once the armed blob has decoded enough to start, or after
   * `timeoutMs` regardless. Lets the handoff wait for the element to be ready
   * BEFORE it becomes audible, so the switchover has no gap.
   */
  whenPlayable(timeoutMs?: number): Promise<void>;
  currentTime(): number;
  /**
   * Decoded length of the armed blob, or 0 while metadata is still loading.
   *
   * Exists so the lock screen can be told a position and a duration measured by
   * the SAME clock. Pairing this element's currentTime with the live player's
   * duration is what made the iOS scrubber jump (B-260728).
   */
  duration(): number;
  /** Pause, clear src, revoke the object-URL. */
  teardown(): void;
}

export function createBackgroundAudio(doc: Document = document): BackgroundAudio {
  const el = doc.createElement('audio');
  el.preload = 'auto';
  el.hidden = true;
  // The element MUST be IN the document. A detached one still plays — which is
  // why this went unnoticed for so long — but WebKit only wires its Now Playing
  // session to media elements that are in the document. Detached, the iOS lock
  // screen had no timeline at all: both times rendered "--:--" and the scrubber
  // had no fill, so whatever it drew moved on its own. Measured in the simulator
  // before this line existed (B-260728).
  doc.body.appendChild(el);

  let objectUrl: string | null = null;

  return {
    arm(blob: Blob, positionSec: number): void {
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
        objectUrl = null;
      }
      objectUrl = URL.createObjectURL(blob);
      el.src = objectUrl;
      el.currentTime = positionSec;
    },

    play(): void {
      void el.play()?.catch(() => { /* autoplay/gesture restrictions — ignore */ });
    },

    pause(): void {
      el.pause();
    },

    seek(sec: number): void {
      el.currentTime = sec;
    },

    setMuted(muted: boolean): void {
      el.muted = muted;
    },

    whenPlayable(timeoutMs = 5000): Promise<void> {
      // HAVE_FUTURE_DATA — enough decoded to start without stalling immediately.
      if (el.readyState >= 3) return Promise.resolve();
      return new Promise((resolve) => {
        let settled = false;
        const finish = (): void => {
          if (settled) return;
          settled = true;
          el.removeEventListener('canplay', finish);
          el.removeEventListener('canplaythrough', finish);
          clearTimeout(timer);
          resolve();
        };
        // Never hang the handoff on a blob that refuses to decode: a late,
        // possibly-gappy start beats a lock screen that stays silent forever.
        const timer = setTimeout(finish, timeoutMs);
        el.addEventListener('canplay', finish);
        el.addEventListener('canplaythrough', finish);
      });
    },

    currentTime(): number {
      return el.currentTime;
    },

    duration(): number {
      // NaN before loadedmetadata, Infinity for streams — neither is a length a
      // caller can do arithmetic on, so both collapse to 0 ("not known yet").
      const d = el.duration;
      return Number.isFinite(d) && d > 0 ? d : 0;
    },

    teardown(): void {
      el.pause();
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
        objectUrl = null;
      }
      el.src = '';
    },
  };
}
